/**
 * ServiceAgreementContent - 跨端公共内容组件
 *
 * 服务协议更新的可滚动内容部分，Web 和 App 共用
 * 不包含 Modal 容器，只负责内容展示
 */

import React from "react";
import { View, Text, ScrollView, StyleSheet, TouchableOpacity } from "../platformComponents";
import { i18nCrossUtil } from "@adgm/libs";

// 跨端通用的颜色定义
const colors = {
  white: "#FFFFFF",
  whiteOp60: "rgba(255, 255, 255, 0.6)",
  whiteOp80: "rgba(255, 255, 255, 0.8)",
  orange: "#F17E00",
  divider: "#3D3F4B",
};

// 跨端通用的字体样式
const typography = {
  title: {
    fontSize: 20,
    lineHeight: 22,
    fontFamily: "DINPro-Bold",
    fontWeight: "700" as const,
  },
  paragraphIntro: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: "DINPro-Regular",
    fontWeight: "400" as const,
  },
  regularText: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: "DINPro-Regular",
    fontWeight: "400" as const,
  },
};

/**
 * ServiceAgreementContent Props
 */
interface ServiceAgreementContentProps {
  /**
   * 滚动区域高度
   * Native: 412px, Web: 335px
   */
  scrollViewHeight?: number;

  /**
   * 标题顶部外边距
   * Native: 10px, Web: 0px (ModalBase自带padding)
   */
  titleMarginTop?: number;
}

/**
 * 服务协议内容组件
 *
 * 跨端公用的内容展示部分，包括：
 * - 标题
 * - 可滚动的协议内容（介绍、确认声明、列表、底部声明）
 */
export const ServiceAgreementContent: React.FC<ServiceAgreementContentProps> = ({
  scrollViewHeight = 335,
  titleMarginTop = 0,
}) => {
  const crossIntl = i18nCrossUtil.t();

  // 列表项的 i18n key 数组
  const listItemKeys = [
    "agreement.service.update.content.item1",
    "agreement.service.update.content.item2",
    "agreement.service.update.content.item3",
    "agreement.service.update.content.item4",
  ] as const;

  return (
    <>
      {/* 标题 */}
      <Text style={[styles.title, { marginTop: titleMarginTop }]}>
        {crossIntl["agreement.service.update.title"]}
      </Text>

      {/* 可滚动内容区域 */}
      <ScrollView
        style={[styles.scrollView, { height: scrollViewHeight }]}
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled>
        <TouchableOpacity activeOpacity={1} style={styles.scrollContent}>
          {/* 介绍段落 */}
          <Text style={styles.paragraphFirst}>
            {crossIntl["agreement.service.update.content.intro"]}
          </Text>

          {/* 确认声明 */}
          <Text style={styles.paragraph}>
            {crossIntl["agreement.service.update.content.acknowledge"]}
          </Text>

          {/* 列表项 */}
          <View style={styles.listContainer}>
            {listItemKeys.map(key => (
              <View key={key} style={styles.listItem}>
                <Text style={styles.bullet}>•</Text>
                <Text style={styles.listItemText}>{crossIntl[key]}</Text>
              </View>
            ))}
          </View>

          {/* 底部声明 */}
          <Text style={styles.paragraphLast}>
            {crossIntl["agreement.service.update.content.footer"]}
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  title: {
    ...typography.title,
    fontSize: 20,
    lineHeight: 22,
    color: colors.white,
  },
  scrollView: {
    marginTop: 24,
  },
  scrollContent: {
    paddingBottom: 16,
  },
  paragraphFirst: {
    ...typography.paragraphIntro,
    color: colors.whiteOp80,
    marginBottom: 16,
  },
  paragraph: {
    ...typography.paragraphIntro,
    color: colors.whiteOp80,
    marginBottom: 16,
  },
  paragraphLast: {
    ...typography.paragraphIntro,
    color: colors.whiteOp80,
    marginBottom: 0,
  },
  listContainer: {
    marginBottom: 16,
  },
  listItem: {
    flexDirection: "row",
    marginBottom: 0,
  },
  bullet: {
    ...typography.regularText,
    color: colors.white,
    marginRight: 4,
  },
  listItemText: {
    ...typography.regularText,
    color: colors.white,
    flex: 1,
  },
});
